import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AiInsightsSection from "@/components/AiInsightsSection";
import PoolAnalyzerWidget from "@/components/PoolAnalyzerWidget";
import AdminHeroBanner from "@/components/AdminHeroBanner";
import AdvertBanner from "@/components/AdvertBanner";
import CongratsBanner from "@/components/CongratsBanner";
import WeeklyPromoCard from "@/components/WeeklyPromoCard";
import { WHATSAPP_SALES_URL } from "@/lib/sales";
import axios from "axios";
import { Trophy, ChevronRight, Clock, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const catLabel = { blog: "Blog", free_pick: "Free Pick", pool_draws: "Pool Draws", pool_result: "Pool Result" };
const catClass = { blog: "cat-blog", free_pick: "cat-free_pick", pool_draws: "cat-pool_draws", pool_result: "cat-pool_result" };

const SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1764703666646-acc2f7d48857?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxmb290YmFsbCUyMHN0YWRpdW0lMjBwaXRjaCUyMGdyZWVufGVufDB8fHx8MTc3NjEwOTk4MHww&ixlib=rb-4.1.0&q=85",
    title: "Expert Fixed Draw Predictions",
    subtitle: "Get accurate football pool draw predictions delivered to you. Buy once, view once, win big.",
    badge: "Weekend Draws Available"
  },
  {
    image: "https://images.unsplash.com/photo-1729843352938-0e10fbf96585?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBwaXRjaCUyMGdyZWVufGVufDB8fHx8MTc3NjEwOTk4MHww&ixlib=rb-4.1.0&q=85",
    title: "This Weekend's Fixed Draws",
    subtitle: "Secure your one-time code and unlock premium predictions from our expert analysts.",
    badge: "Premium Picks"
  },
  {
    image: "https://images.unsplash.com/photo-1775725181887-b3b870a015c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxmb290YmFsbCUyMHN0YWRpdW0lMjBwaXRjaCUyMGdyZWVufGVufDB8fHx8MTc3NjEwOTk4MHww&ixlib=rb-4.1.0&q=85",
    title: "Trusted by Thousands",
    subtitle: "Join our community of winners. Pool draws, results, and expert analysis all in one place.",
    badge: "Proven Track Record"
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts`).then(r => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const freePicks = posts.filter(p => p.category === "free_pick");
  const latestPosts = posts.filter(p => ["blog", "pool_draws", "pool_result"].includes(p.category)).slice(0, 6);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("/api")) return `${BACKEND}${url}`;
    return url;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false })
  );

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="home-page">
      <SEO title="Expert Football Pool Draw Predictions" description="Get expert fixed draw predictions for UK football pools. Nap 3/3, Perm 3/4, Perm 3/5, Perm 4/5. Weekend pool draws with secure one-time code. Trusted by thousands of punters." keywords="fixed draws, pool draws, football predictions, Nap 3/3, Perm 3/4, Perm 3/5, Perm 4/5, weekend draws, UK pools, pool result, score draw predictions" path="/" />
      <Navbar />

      {/* Hero Banner (admin-editable, replaces former slider) */}
      <AdminHeroBanner />

      {/* Sitewide advert banner — under the hero */}
      <div className="px-4 pt-6"><div className="max-w-6xl mx-auto"><AdvertBanner /></div></div>

      {/* Congratulations banner (admin-editable, sits between hero & weekly promo) */}
      <CongratsBanner />

      {/* Weekly Promo — big bold call-out with featured coupon numbers (admin-editable) */}
      <WeeklyPromoCard />

      {/* AI Pool Analyzer — visitor pastes their pool numbers */}
      <PoolAnalyzerWidget />

      {/* Free Picks Section */}
      {freePicks.length > 0 && (
        <section className="py-12 px-4" data-testid="free-picks-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Free Picks</h2>
              <Badge className="cat-free_pick">Free</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {freePicks.slice(0, 6).map(post => (
                <Card key={post.post_id} className="bg-white border-gray-200 card-hover cursor-pointer overflow-hidden group" onClick={() => navigate(`/post/${post.post_id}`)} data-testid={`free-pick-${post.post_id}`}>
                  {post.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img src={getImageUrl(post.image_url)} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <Badge className="cat-free_pick text-xs mb-3">Free Pick</Badge>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-sm text-gray-500 line-clamp-3">{stripHtml(post.content)}</p>
                    <div className="mt-3 flex items-center text-xs text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AI Insights — replaces the free tips/preview section */}
      <AiInsightsSection />

      {/* Buy on WhatsApp CTA */}
      <section className="py-10 px-4" data-testid="whatsapp-sales-section">
        <div className="max-w-3xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-400 shadow-xl p-6 sm:p-8 text-center" style={{ background: "linear-gradient(135deg, #052e16 0%, #065f46 50%, #047857 100%)" }}>
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-amber-300/20 blur-3xl" />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.35)" }}>Ready to Win This Weekend?</h2>
            <p className="text-emerald-50/90 text-sm sm:text-base mb-5 max-w-xl mx-auto">
              Chat Madam Precious directly on WhatsApp to buy this week&apos;s premium picks. Instant delivery, personal service.
            </p>
            <a
              href={WHATSAPP_SALES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1DAE55] text-white font-extrabold px-7 py-3.5 shadow-lg shadow-emerald-500/30 transition-transform hover:scale-[1.03] active:scale-100"
              data-testid="home-whatsapp-cta"
            >
              <MessageCircle className="h-5 w-5" /> Buy Games on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Latest Posts Section */}
      {latestPosts.length > 0 && (
        <section className="py-12 px-4" data-testid="latest-posts-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Latest Posts</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestPosts.map(post => (
                <Card key={post.post_id} className="bg-white border-gray-200 overflow-hidden group hover:shadow-lg transition-shadow" data-testid={`post-${post.post_id}`}>
                  {post.image_url && (
                    <Link to={`/post/${post.post_id}`}>
                      <div className="aspect-video overflow-hidden">
                        <img src={getImageUrl(post.image_url)} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    </Link>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={`${catClass[post.category] || "cat-blog"} text-xs`}>
                        {catLabel[post.category] || "Blog"}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2">{post.title}</h3>
                    <p className="text-sm text-gray-500 line-clamp-3 mb-4">{stripHtml(post.content)}</p>
                    <Link to={`/post/${post.post_id}`} className="text-sm text-green-600 font-medium flex items-center hover:underline" data-testid={`read-more-${post.post_id}`}>
                      Read More <ChevronRight className="h-4 w-4 ml-0.5" />
                    </Link>
                    <Button
                      onClick={() => window.open(WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer")}
                      className="w-full mt-4 bg-[#25D366] hover:bg-[#1DAE55] text-white font-bold rounded-full"
                      data-testid={`buy-btn-${post.post_id}`}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" /> Buy on WhatsApp
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!loading && posts.length === 0 && (
        <section className="py-20 px-4 text-center">
          <div className="max-w-md mx-auto">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-green-600 opacity-40" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h2>
            <p className="text-gray-500 mb-6">New predictions and posts will be published here. Check back soon!</p>
            <Button onClick={() => window.open(WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer")} className="bg-[#25D366] hover:bg-[#1DAE55] text-white rounded-full px-6" data-testid="empty-buy-btn">
              <MessageCircle className="h-4 w-4 mr-2" /> Buy on WhatsApp
            </Button>
          </div>
        </section>
      )}

      {/* Pre-footer advert banner (home page only) */}
      <div className="px-4 pt-6 pb-2" data-testid="home-footer-ad-wrap">
        <div className="max-w-6xl mx-auto">
          <AdvertBanner />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
